import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import './bolt-loader.css';

const BOLT_PATH =
  'M124.79 110.45C124.743 110.517 124.715 110.596 124.71 110.678C124.706 110.76 124.724 110.842 124.763 110.915C124.802 110.988 124.861 111.05 124.932 111.093C125.004 111.136 125.086 111.159 125.17 111.16C150.857 111.18 172.953 111.16 191.46 111.1C198.63 111.08 202.02 118.69 198.64 124.78C197.66 126.527 195.643 129.503 192.59 133.71C188.803 138.923 175.557 157.52 152.85 189.5C146.83 197.97 139.54 209.32 132.7 219.08C107.9 254.48 93.9766 274.46 90.9299 279.02C88.4499 282.727 85.2399 287.47 81.2999 293.25C76.7066 299.983 73.4399 304.683 71.4999 307.35C66.7399 313.897 62.33 320.3 58.27 326.56C57.12 328.32 55.17 330.36 53.82 332.42C46.9666 342.873 41.07 351.573 36.13 358.52C32.52 363.59 27.4499 365.88 21.6799 362.69C18.3399 360.85 18.7099 354.3 19.8399 351.41C20.6799 349.243 21.2566 347.567 21.5699 346.38C23.6299 338.587 24.8833 334.017 25.3299 332.67C28.3766 323.483 31 315.037 33.2 307.33C33.64 305.79 35.2 300.55 37.88 291.61C38.62 289.157 39.3599 286.733 40.0999 284.34C41.9999 278.22 43.5233 273.647 44.6699 270.62C46.5299 265.69 47.15 261.04 48.79 256.22C52.63 244.973 56.6366 232.92 60.81 220.06C62.3166 215.42 63.5833 211.177 64.6099 207.33C66.6766 199.603 67.86 195.323 68.16 194.49C68.44 193.703 68.79 193.01 69.21 192.41C69.2681 192.329 69.3025 192.234 69.3092 192.135C69.3158 192.036 69.2945 191.937 69.2475 191.849C69.2006 191.762 69.13 191.689 69.0438 191.64C68.9576 191.59 68.8592 191.566 68.7599 191.57C61.4799 191.777 56.08 191.757 52.56 191.51C44.44 190.943 39.0499 190.687 36.3899 190.74C28.7033 190.887 19.9866 190.853 10.2399 190.64C6.81995 190.57 2.49995 190.26 0.989949 186.92C-1.29005 181.87 0.919949 178.8 2.11995 174.04C4.33328 165.26 7.25328 154.087 10.8799 140.52C14.6699 126.37 19.5399 111.28 22.6099 97.61C23.3433 94.3633 24.1899 91.23 25.1499 88.21C27.4699 80.9567 29.85 72.5267 32.29 62.92C33.4366 58.4067 34.6766 54.0667 36.0099 49.9C37.9399 43.87 38.88 38.28 40.75 32.47C43.5633 23.73 45.7699 15.58 47.3699 8.02C48.5899 2.31 51.4999 0.05 57.4799 0.03C61.0999 0.01 97.5233 0 166.75 0C169.3 0 173.22 0.82 174.3 3.84C175.62 7.54 175.12 10.53 173.24 14.16C169.38 21.6333 165.883 28.8133 162.75 35.7C161.063 39.4133 158.667 44.0367 155.56 49.57C149.36 60.63 144 73.05 138.08 83.73C133.32 92.3167 129.553 99.97 126.78 106.69C126.2 108.09 125.537 109.343 124.79 110.45Z';

const boltLoaderVariants = cva('bolt-loader', {
  variants: {
    size: {
      xs: 'bolt-loader--xs',
      sm: 'bolt-loader--sm',
      md: 'bolt-loader--md',
      lg: 'bolt-loader--lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export interface BoltLoaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof boltLoaderVariants> {
  ringAndSpin?: boolean;
}

export const BoltLoader = ({
  size,
  ringAndSpin = true,
  className,
  ...props
}: BoltLoaderProps) => {
  const clipId = React.useId().replace(/:/g, '');

  return (
    <div
      className={cn(
        boltLoaderVariants({ size }),
        ringAndSpin && 'bolt-loader--ring-and-spin',
        className,
      )}
      {...props}
    >
      <svg
        className="bolt-svg"
        viewBox={ringAndSpin ? '-40 -40 280 445' : '0 0 200 365'}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        {ringAndSpin ? <circle className="bolt-ring" cx="100" cy="182.5" r="228" /> : null}
        <g className="bolt-inner">
          <defs>
            <clipPath id={`bolt-fill-clip-${clipId}`} clipPathUnits="userSpaceOnUse">
              <rect className="bolt-fill-clip-rect" x="0" y="0" width="200" height="365" />
            </clipPath>
          </defs>
          <path className="bolt-bg" fill="#333333" d={BOLT_PATH} />
          <path
            className="bolt-fill"
            fill="#E5AC47"
            d={BOLT_PATH}
            clipPath={`url(#bolt-fill-clip-${clipId})`}
          />
        </g>
      </svg>
    </div>
  );
};
